import { FeedbackAlert as Alert } from "src/shared/ui/src/feedback/FeedbackAlert";
import { DesignDialog as Dialog } from "src/shared/ui/src/design/DesignDialog";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import LicenseTermsService, {
  LicenseTermsAcceptanceState,
  resolveLicenseTermsDocumentUrl,
} from "src/services/licenseTermsService";
import { useSnack } from "src/shared/ui/src/contexts/SnackContext";
import dico from "src/shared/util/src/language/dico";
import { ROLE_NAME, userHasRole } from "src/shared/util/src/roles/role-access";
import { useUserStore } from "src/stores/userStore";

interface LicenseTermsAcceptanceContextValue {
  state: LicenseTermsAcceptanceState | null;
  isLoading: boolean;
  requiresAcceptance: boolean;
  refresh: () => Promise<void>;
}

const LicenseTermsAcceptanceContext =
  createContext<LicenseTermsAcceptanceContextValue | null>(null);

export const LicenseTermsAcceptanceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const myDico = dico();
  const termsDico = myDico.licensedPortal.licenseTermsAcceptance;
  const location = useLocation();
  const { token, currentUser, fetchUser, signOut } = useUserStore();
  const { showSnack } = useSnack();
  const [state, setState] = useState<LicenseTermsAcceptanceState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [refused, setRefused] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);
  const isProtectedAppRoute =
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/") ||
    location.pathname === "/licensed" ||
    location.pathname.startsWith("/licensed/");

  useEffect(() => {
    if (!token || currentUser || !isProtectedAppRoute) {
      return;
    }

    setIsLoadingUser(true);
    fetchUser(token).finally(() => {
      setIsLoadingUser(false);
    });
  }, [currentUser, fetchUser, isProtectedAppRoute, token]);

  const refresh = useCallback(async () => {
    if (!token || !currentUser) {
      setState(null);
      setHasChecked(false);
      return;
    }
    // Speaker / DisplayEditor / Admin never own license CGU - skip the API call
    const bypassTermsFetch =
      userHasRole(currentUser, ROLE_NAME.Admin) ||
      userHasRole(currentUser, ROLE_NAME.Speaker) ||
      userHasRole(currentUser, ROLE_NAME.DisplayEditor);
    if (bypassTermsFetch) {
      setState({
        requiresAcceptance: false,
        hasAccepted: true,
        license: null,
        termsVersion: null,
        acceptance: null,
        acceptances: [],
      });
      setHasChecked(true);
      setRefused(false);
      setIsChecked(false);
      return;
    }
    setIsLoading(true);
    try {
      const nextState = await LicenseTermsService.fetchCurrent(token);
      setState(nextState);
      setHasChecked(true);
      if (!nextState.requiresAcceptance) {
        setRefused(false);
        setIsChecked(false);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : termsDico.verifyError;
      showSnack(message, "error");
      setHasChecked(true);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, showSnack, termsDico.verifyError, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const requiresAcceptance = Boolean(state?.requiresAcceptance);

  const handleAccept = async () => {
    if (!token || !isChecked) {
      return;
    }
    setIsAccepting(true);
    try {
      const nextState = await LicenseTermsService.acceptCurrent(token);
      setState(nextState);
      setRefused(false);
      setIsChecked(false);
      showSnack(termsDico.acceptSuccess, "success");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : termsDico.acceptError;
      showSnack(message, "error");
    } finally {
      setIsAccepting(false);
    }
  };

  const value = useMemo(
    () => ({
      state,
      isLoading,
      requiresAcceptance,
      refresh,
    }),
    [isLoading, refresh, requiresAcceptance, state],
  );

  const terms = state?.termsVersion ?? null;
  const termsDocumentUrl = terms
    ? resolveLicenseTermsDocumentUrl(terms.documentUrl)
    : "";

  useEffect(() => {
    if (!termsDocumentUrl) {
      setPdfPreviewUrl("");
      setPdfPreviewError(null);
      return;
    }

    let isCancelled = false;
    let objectUrl = "";

    setPdfPreviewUrl("");
    setPdfPreviewError(null);

    fetch(termsDocumentUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP_${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (isCancelled) return;
        objectUrl = URL.createObjectURL(
          blob.type === "application/pdf"
            ? blob
            : new Blob([blob], { type: "application/pdf" }),
        );
        setPdfPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (isCancelled) return;
        setPdfPreviewError(termsDico.pdfPreviewUnavailable);
      });

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [termsDico.pdfPreviewUnavailable, termsDocumentUrl]);

  const open = Boolean(
    token && currentUser && hasChecked && requiresAcceptance,
  );
  const shouldHoldProtectedRoute =
    isProtectedAppRoute &&
    Boolean(token) &&
    (!currentUser ||
      isLoadingUser ||
      Boolean(currentUser && (!hasChecked || isLoading || requiresAcceptance)));

  return (
    <LicenseTermsAcceptanceContext.Provider value={value}>
      {shouldHoldProtectedRoute ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          {!open && <CircularProgress />}
        </Box>
      ) : (
        children
      )}
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        aria-labelledby="license-terms-dialog-title"
      >
        <DialogTitle id="license-terms-dialog-title">
          {termsDico.dialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {termsDico.intro}
          </Typography>

          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoading && !terms && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {termsDico.noActiveVersion}
            </Alert>
          )}

          {terms && (
            <>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  height: { xs: 360, md: 520 },
                  overflow: "hidden",
                  mb: 2,
                  backgroundColor: "background.default",
                }}
              >
                {pdfPreviewUrl ? (
                  <iframe
                    title={terms.title}
                    src={pdfPreviewUrl}
                    style={{ border: 0, width: "100%", height: "100%" }}
                  />
                ) : (
                  <Box
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      height: "100%",
                      justifyContent: "center",
                      p: 2,
                    }}
                  >
                    {pdfPreviewError ? (
                      <Alert severity="warning">{pdfPreviewError}</Alert>
                    ) : (
                      <CircularProgress />
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {termsDico.versionLabel} : <strong>{terms.version}</strong>
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {termsDico.hashLabel} : <strong>{terms.termsHash}</strong>
                </Typography>
                <Link href={termsDocumentUrl} target="_blank" rel="noreferrer">
                  {termsDico.openPdfLink}
                </Link>
              </Box>
            </>
          )}

          {refused && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {termsDico.refusalMessage}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(event) => setIsChecked(event.target.checked)}
              />
            }
            label={termsDico.checkboxLabel}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefused(true)} disabled={isAccepting}>
            {termsDico.refuseButton}
          </Button>
          {refused && (
            <Button color="inherit" onClick={signOut} disabled={isAccepting}>
              {termsDico.signOutButton}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleAccept}
            disabled={!isChecked || !terms || isAccepting}
            startIcon={isAccepting ? <CircularProgress size={18} /> : undefined}
          >
            {termsDico.acceptButton}
          </Button>
        </DialogActions>
      </Dialog>
    </LicenseTermsAcceptanceContext.Provider>
  );
};

export const useLicenseTermsAcceptance = () => {
  const context = useContext(LicenseTermsAcceptanceContext);
  if (!context) {
    throw new Error(
      "useLicenseTermsAcceptance must be used inside LicenseTermsAcceptanceProvider",
    );
  }
  return context;
};

export const LicensedTermsRouteGuard = () => {
  const location = useLocation();
  const { token, currentUser } = useUserStore();
  const { requiresAcceptance } = useLicenseTermsAcceptance();

  if (!token || !currentUser) {
    return <Outlet />;
  }

  // Admin / Speaker / DisplayEditor do not accept license terms as owners
  const bypassTerms =
    userHasRole(currentUser, ROLE_NAME.Admin) ||
    userHasRole(currentUser, ROLE_NAME.Speaker) ||
    userHasRole(currentUser, ROLE_NAME.DisplayEditor);

  if (!bypassTerms && requiresAcceptance && location.pathname !== "/licensed") {
    return <Navigate to="/licensed" replace />;
  }

  return <Outlet />;
};
